
// ── Mock Data ────────────────────────────────────────────────────────────────

const inventory = [
  { id: 1, name: 'Smart Oven Pro x4', category: 'Kitchen', health: 98, status: 'Optimal' },
  { id: 2, name: 'Industrial Dishwasher', category: 'Cleaning', health: 72, status: 'Maintenance Required' },
  { id: 3, name: 'Refrigeration Unit 7', category: 'Storage', health: 45, status: 'Critical' },
  { id: 4, name: 'Point of Sale Terminal', category: 'IT', health: 100, status: 'Optimal' },
]

// ── Components ───────────────────────────────────────────────────────────────

export default function MotionTemplate2() {
  return (
    <div className="space-y-12 pb-20 animate-[fade-in-up_0.8s_cubic-bezier(0.16,1,0.3,1)]">
      {/* 1. Dynamic Resource Header */}
      <div className="relative p-12 bg-white border border-slate-100 rounded-[3rem] shadow-[0_30px_60px_rgba(0,0,0,0.02)] overflow-hidden group">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
           <div>
              <h1 className="text-5xl font-bold tracking-tighter text-slate-900 mb-2">Fleet Intelligence</h1>
              <p className="text-slate-500 font-medium text-lg">Predictive health monitoring for hardware assets.</p>
           </div>
           <div className="flex -space-x-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="w-14 h-14 rounded-full border-4 border-white bg-slate-100 overflow-hidden shadow-sm hover:scale-110 hover:z-20 transition-all cursor-pointer relative group/avatar">
                   <div className="absolute inset-0 bg-primary opacity-0 group-hover/avatar:opacity-20" />
                </div>
              ))}
              <div className="w-14 h-14 rounded-full border-4 border-white bg-primary text-white flex items-center justify-center font-bold shadow-lg hover:scale-110 hover:z-20 transition-all cursor-pointer">+</div>
           </div>
        </div>
      </div>

      {/* 2. Interactive Asset Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {inventory.map((item, i) => (
          <div 
            key={item.id}
            className="group bg-white rounded-[2.5rem] border border-slate-100 p-8 flex flex-col justify-between transition-all duration-700 hover:shadow-[0_40px_80px_rgba(0,0,0,0.05)] hover:-translate-y-4 cursor-pointer relative overflow-hidden"
            style={{ animationDelay: `${i * 100}ms` }}
          >
            <div className={`absolute top-0 left-0 w-full h-1.5 ${item.health < 50 ? 'bg-rose-500' : item.health < 80 ? 'bg-amber-400' : 'bg-teal-500'} scale-x-0 group-hover:scale-x-100 transition-transform duration-1000 origin-left`} />
            
            <div className="mb-8 flex justify-between items-center">
               <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all duration-500 group-hover:rotate-[360deg] shadow-sm">
                  <span className="material-symbols-outlined text-3xl">precision_manufacturing</span>
               </div>
               <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">{item.category}</span>
            </div>

            <div>
               <h3 className="text-2xl font-bold text-slate-800 mb-4 group-hover:text-primary transition-colors leading-tight">{item.name}</h3>
               <div className="flex items-center gap-3 mb-8">
                  <div className="flex-1 h-1 bg-slate-50 rounded-full overflow-hidden">
                     <div className={`h-full ${item.health < 50 ? 'bg-rose-500' : item.health < 80 ? 'bg-amber-400' : 'bg-teal-500'} transition-all duration-[2s] ease-out w-0 group-hover:w-[${item.health}%]`} style={{ width: item.health + '%' }} />
                  </div>
                  <span className="text-[11px] font-black text-slate-400 font-mono">{item.health}%</span>
               </div>
            </div>

            <div className="flex items-center justify-between">
               <span className={`text-[10px] font-black uppercase tracking-widest ${item.health < 50 ? 'text-rose-500' : 'text-slate-400'}`}>{item.status}</span>
               <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all group-hover:translate-x-0 translate-x-4">
                  <span className="material-symbols-outlined !text-sm">settings</span>
               </div>
            </div>
          </div>
        ))}
      </div>

      {/* 3. Advanced Multi-Step Interaction Area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
         <div className="bg-slate-900 rounded-[3rem] p-12 text-white relative group overflow-hidden">
            <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-bl from-teal-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
            <h3 className="text-3xl font-bold tracking-tight mb-8">Service Request</h3>
            <div className="space-y-6">
               <div className="space-y-2">
                  <label className="text-[10px] font-black text-white/30 uppercase tracking-widest">Asset Category</label>
                  <div className="flex gap-2">
                     {['Kitchen', 'IT', 'Logistics'].map(cat => (
                       <div key={cat} className="px-4 py-2 bg-white/5 rounded-xl border border-white/5 text-xs font-bold hover:bg-white/10 cursor-pointer transition-colors">{cat}</div>
                     ))}
                  </div>
               </div>
               <div className="space-y-2">
                  <label className="text-[10px] font-black text-white/30 uppercase tracking-widest">Problem Description</label>
                  <textarea className="w-full bg-white/5 border border-white/5 rounded-2xl p-5 text-sm outline-none focus:ring-4 ring-teal-500/20 transition-all h-32 resize-none" placeholder="Describe the failure..." />
               </div>
               <button className="w-full py-4 bg-teal-500 text-slate-900 font-black rounded-2xl uppercase tracking-widest shadow-xl shadow-teal-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all">Submit Critical Alert</button>
            </div>
         </div>

         <div className="bg-white rounded-[3rem] border border-slate-100 p-12 flex flex-col shadow-[0_40px_80px_rgba(0,0,0,0.02)] relative overflow-hidden">
            <h3 className="text-3xl font-bold tracking-tight mb-8">Maintenance Schedule</h3>
            <div className="flex-1 space-y-6">
               {[1, 2, 3].map(i => (
                 <div key={i} className="flex items-center gap-6 p-4 rounded-2xl hover:bg-slate-50 transition-colors group/row cursor-pointer">
                    <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center group-hover/row:bg-primary transition-colors">
                       <span className="material-symbols-outlined text-slate-400 group-hover/row:text-white">event</span>
                    </div>
                    <div className="flex-1 min-w-0">
                       <p className="font-bold text-slate-800">Weekly Sensor Calibration</p>
                       <p className="text-xs text-slate-400 font-medium">Monday, 09:00 AM · Admin HQ</p>
                    </div>
                    <span className="material-symbols-outlined text-slate-200 group-hover/row:text-primary transition-colors">check_circle</span>
                 </div>
               ))}
            </div>
            <div className="mt-8 pt-8 border-t border-slate-50 flex justify-between items-center">
               <p className="text-[11px] font-black text-slate-300 uppercase tracking-widest">Next Auto-Run in 4h 12m</p>
               <button className="text-primary font-black text-[11px] uppercase tracking-widest hover:underline">View All</button>
            </div>
         </div>
      </div>
    </div>
  )
}
